/**
 * ColorPicker.jsx — Cheddar Terminal's own picker, replacing the native
 * <input type="color"> popup (white, unstylable, theme-deaf).
 *
 * Shade square (saturation/brightness) + hue bar + ONE hex input + swatch.
 * Dark panel matching the terminal.
 *
 * Internal HSV state is the source of truth while interacting: hex alone
 * can't represent hue for grays (s=0) or black (v=0), and deriving from
 * hex on every render is the classic bug where touching a gray snaps the
 * hue bar back to red. External `value` changes re-sync the internals.
 *
 * Usage:
 *   <ColorPicker value={hex} onChange={setHex} />          // swatch trigger,
 *     panel opens on click, closes on outside click — same UX shape as the
 *     native input this replaces, so call sites need nothing extra.
 *   <ColorPicker value={hex} onChange={setHex} inline />   // bare open panel
 *   size={16} — trigger square px (default 16)
 */

import { useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

const clamp01 = (v) => Math.max(0, Math.min(1, v))

// Touch device? Scale for fingers: taller pick areas, bigger thumbs, and a
// 16px input font — anything smaller makes iOS zoom the page on focus.
const COARSE = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches
const PANEL_W = COARSE ? Math.min(260, (typeof window !== 'undefined' ? window.innerWidth : 320) - 16) : 216
const SQ_H    = COARSE ? 150 : 122
const BAR_H   = COARSE ? 22 : 14
const THUMB   = COARSE ? 24 : 18
const SQTHUMB = COARSE ? 20 : 14

const hsvToHex = (h, s, v) => {
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c
  let r = 0, g = 0, b = 0
  if (h < 60)       { r = c; g = x }
  else if (h < 120) { r = x; g = c }
  else if (h < 180) { g = c; b = x }
  else if (h < 240) { g = x; b = c }
  else if (h < 300) { r = x; b = c }
  else              { r = c; b = x }
  const to = (n) => Math.round((n + m) * 255).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

const hexToHsv = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!m) return null
  const n = parseInt(m[1], 16)
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d > 0) {
    if (max === r)      h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else                h = (r - g) / d + 4
    h = (h * 60 + 360) % 360
  }
  return { h, s: max === 0 ? 0 : d / max, v: max }
}

const normalizeHex = (raw) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((raw || '').trim())
  return m ? `#${m[1].toLowerCase()}` : null
}

const S = {
  panel: {
    background: '#0d0d16',
    border: '1px solid #333',
    borderRadius: 6,
    padding: 10,
    width: PANEL_W,
    boxSizing: 'border-box',
    fontFamily: 'monospace',
    boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
  },
  sqWrap: { position: 'relative', height: SQ_H, marginBottom: 10, cursor: 'crosshair', touchAction: 'none' },
  sq: (h) => ({
    position: 'absolute', inset: 0,
    borderRadius: 4,
    border: '1px solid #333',
    background: `linear-gradient(to top, #000, rgba(0,0,0,0)), linear-gradient(to right, #fff, hsl(${h}, 100%, 50%))`,
  }),
  sqThumb: (s, v, color) => ({
    position: 'absolute',
    left: `${s * 100}%`, top: `${(1 - v) * 100}%`,
    transform: 'translate(-50%, -50%)',
    width: SQTHUMB, height: SQTHUMB, borderRadius: '50%',
    background: color,
    border: '2px solid #e0e0e0',
    boxShadow: '0 0 4px rgba(0,0,0,0.8)',
    pointerEvents: 'none',
  }),
  barWrap: { position: 'relative', height: BAR_H, cursor: 'pointer', marginBottom: 10, touchAction: 'none' },
  bar: {
    position: 'absolute', inset: 0,
    borderRadius: BAR_H / 2,
    border: '1px solid #333',
    background: 'linear-gradient(to right,#f00 0%,#ff0 16.7%,#0f0 33.3%,#0ff 50%,#00f 66.7%,#f0f 83.3%,#f00 100%)',
  },
  barThumb: (leftPct, h) => ({
    position: 'absolute', top: '50%', left: `${leftPct}%`,
    transform: 'translate(-50%, -50%)',
    width: THUMB, height: THUMB, borderRadius: '50%',
    background: `hsl(${h}, 100%, 50%)`,
    border: '2px solid #e0e0e0',
    boxShadow: '0 0 4px rgba(0,0,0,0.8)',
    pointerEvents: 'none',
  }),
  row: { display: 'flex', alignItems: 'center', gap: 8 },
  swatch: {
    width: COARSE ? 42 : 34, height: COARSE ? 42 : 34, flex: 'none',
    borderRadius: 4, border: '1px solid #333',
  },
  input: {
    flex: 1, minWidth: 0,
    background: '#0a0a15',
    border: '1px solid #333',
    borderRadius: 3,
    color: '#e0e0e0',
    fontFamily: 'monospace',
    fontSize: COARSE ? 16 : 13,
    padding: COARSE ? '9px 10px' : '6px 8px',
    outline: 'none',
    textTransform: 'lowercase',
  },
}

export default function ColorPicker({ value = '#00f0ff', onChange, inline = false, size = 16, compact = false, title }) {
  const [open, setOpen] = useState(false)
  const [openUp, setOpenUp] = useState(false)
  const [anchor, setAnchor] = useState(null)
  const wrapRef = useRef(null)
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const [hsv, setHsv] = useState(() => hexToHsv(value) || { h: 180, s: 1, v: 1 })
  const [text, setText] = useState(value)
  const [editing, setEditing] = useState(false)
  const emittedRef = useRef(value)
  const sqRef = useRef(null)
  const barRef = useRef(null)
  const dragRef = useRef(null)   // 'sq' | 'bar' | null

  const hex = hsvToHex(hsv.h, hsv.s, hsv.v)

  // External value change (not our own echo) → re-sync internals.
  // Preserve current hue when the incoming color can't express one (grays).
  useEffect(() => {
    if (value === emittedRef.current) return
    const next = hexToHsv(value)
    if (next) setHsv(prev => ({ ...next, h: next.s === 0 ? prev.h : next.h }))
  }, [value])

  useEffect(() => { if (!editing) setText(hex) }, [hex, editing])

  const emit = (nextHsv) => {
    setHsv(nextHsv)
    const out = hsvToHex(nextHsv.h, nextHsv.s, nextHsv.v)
    emittedRef.current = out
    onChange?.(out)
  }

  const pickSq = (e) => {
    const r = sqRef.current.getBoundingClientRect()
    emit({
      h: hsv.h,
      s: clamp01((e.clientX - r.left) / r.width),
      v: clamp01(1 - (e.clientY - r.top) / r.height),
    })
  }
  const pickBar = (e) => {
    const r = barRef.current.getBoundingClientRect()
    emit({ ...hsv, h: clamp01((e.clientX - r.left) / r.width) * 359.999 })
  }

  const dragHandlers = (kind, pick) => ({
    onPointerDown: (e) => {
      dragRef.current = kind
      e.currentTarget.setPointerCapture?.(e.pointerId)
      pick(e)
    },
    onPointerMove: (e) => { if (dragRef.current === kind) pick(e) },
    onPointerUp: () => { dragRef.current = null },
    onPointerCancel: () => { dragRef.current = null },
  })

  const commitText = (raw) => {
    const norm = normalizeHex(raw)
    if (norm) {
      const next = hexToHsv(norm)
      emittedRef.current = norm
      setHsv(prev => ({ ...next, h: next.s === 0 ? prev.h : next.h }))
      onChange?.(norm)
    } else {
      setText(hex)
    }
  }

  const panel = (
    <div style={S.panel} onMouseDown={(e) => e.stopPropagation()}>
      <div ref={sqRef} style={S.sqWrap} {...dragHandlers('sq', pickSq)}>
        <div style={S.sq(hsv.h)} />
        <div style={S.sqThumb(hsv.s, hsv.v, hex)} />
      </div>
      <div ref={barRef} style={S.barWrap} {...dragHandlers('bar', pickBar)}>
        <div style={S.bar} />
        <div style={S.barThumb(hsv.h / 3.6, hsv.h)} />
      </div>
      <div style={S.row}>
        <div style={{ ...S.swatch, background: hex }} />
        <input
          style={S.input}
          value={text}
          spellCheck={false}
          onFocus={() => setEditing(true)}
          onChange={(e) => {
            setText(e.target.value)
            if (normalizeHex(e.target.value)) commitText(e.target.value)
          }}
          onBlur={(e) => { setEditing(false); commitText(e.target.value) }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }}
        />
      </div>
    </div>
  )

  if (inline) return panel

  const sq = compact ? (size || 11) : size
  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={(e) => {
          // Hosts render this inside rows with their own onClick (theme
          // switch / popover close). The native inputs this replaces
          // stopped propagation; so must we, or the click closes the very
          // popover we live in before the panel can mount.
          e.stopPropagation()
          if (!open && wrapRef.current) {
            const r = wrapRef.current.getBoundingClientRect()
            const up = r.bottom + (SQ_H + BAR_H + 130) > window.innerHeight
            setOpenUp(up)
            setAnchor({
              left: Math.max(8, Math.min(r.left, window.innerWidth - PANEL_W - 8)),
              top: r.bottom + 4,
              bottomUp: window.innerHeight - r.top + 4,
            })
          }
          setOpen(o => !o)
        }}
        title={title || hex}
        style={{
          width: sq, height: sq, padding: 0,
          background: hex,
          border: compact ? '1px solid #00000060' : '1px solid #333',
          borderRadius: compact ? 0 : 3,
          cursor: 'pointer', display: 'inline-block', verticalAlign: 'middle',
        }}
      />
      {open && anchor && createPortal(
        <span
          data-cm-keep=""   /* ChartMenu: taps in this portaled panel are not "outside" (phone touchstart close) */
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: anchor.left,
            ...(openUp ? { bottom: anchor.bottomUp } : { top: anchor.top }),
            // Above every chart menu (styles.css CHART MENUS: --menu-z).
            zIndex: 'var(--menu-picker-z, 10000)',
          }}
        >
          {panel}
        </span>,
        document.body
      )}
    </span>
  )
}